// src/components/PDFFullPagePreview.tsx
import { Download, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface PDFFullPagePreviewProps {
  pdfBlob: Blob;
  fileName: string;
  onClose: () => void;
}

export const PDFFullPagePreview = ({ pdfBlob, fileName, onClose }: PDFFullPagePreviewProps) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const isClosingRef = useRef(false);

  const handleClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    onClose();
  }, [onClose]);

  // Setup PDF URL and history
  useEffect(() => {
    const url = URL.createObjectURL(pdfBlob);
    setPdfUrl(url);

    const originalTitle = document.title;
    document.title = fileName;

    // Push state to history
    window.history.pushState({ pdfPreview: true, timestamp: Date.now() }, '');

    return () => {
      URL.revokeObjectURL(url);
      document.title = originalTitle;
      isClosingRef.current = false;
    };
  }, [pdfBlob, fileName]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      handleClose();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handleClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      console.log('e.key', e.key);
      if (e.key === 'Escape') {
        e.preventDefault();
        window.history.back();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Prevent scroll on body when preview is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      link.click();
    }
  };

  const handleCloseClick = () => {
    window.history.back();
  };

  if (!pdfUrl) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px',
          backgroundColor: '#2d2d2d',
          borderBottom: '1px solid #404040',
        }}
      >
        <span style={{ color: '#fff', fontSize: '16px', fontWeight: 500 }}>{fileName}</span>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleDownload}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '36px',
            }}
          >
            <span>Download</span>
            <Download className="w-4 h-4 ml-2" />
          </button>

          <button
            onClick={handleCloseClick}
            style={{
              padding: '8px 16px',
              backgroundColor: '#525252',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '36px',
            }}
          >
            <span>Close</span>
            <X className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>

      {/* PDF Viewer */}
      <iframe
        src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
        style={{
          flex: 1,
          width: '100%',
          border: 'none',
        }}
        title={fileName}
      />
    </div>,
    document.body
  );
};
